import './MenuItemCard.css';

import { useState } from 'react';
import { getRandomCalories } from '../utils/utils';
import FoodImage from './FoodImage';
import NutriScoreDisplay from './NutriScoreDisplay';

const MenuItemCard = ({ item, index, onItemClick, onUpgradeClick, userPlan = 'free' }) => {
  const [showUpgradeTooltip, setShowUpgradeTooltip] = useState(false);

  // Check if this is a separator object
  const isSeparator = typeof item === 'object' && item.separator && !item.menuResponse;

  // If it's a separator, render the separator component
  if (isSeparator) {
    return (
      <div className="menu-separator-card">
        <div className="separator-content">
          <h2 className="separator-text">{item.separator}</h2>
          <div className="separator-line"></div>
        </div>
      </div>
    );
  }

  const menuName = typeof item === 'string' ? item : item.menuResponse;
  const imageUrls = typeof item === 'object' ? item.imageUrls : null;
  const nutritionalScore = typeof item === 'object' ? item.nutritional_score : null;
  const approximateCalories = typeof item === 'object' ? item.approximate_calories : null;
  const price = typeof item === 'object' ? item.price : null;
  const description = typeof item === 'object' ? item.description : null;
  const isDemoLocked = typeof item === 'object' ? item.isDemoLocked : false;
  const isLocked = typeof item === 'object' ? item.isLocked : false;

  // Function to get high-risk allergens for paying users
  const getHighRiskAllergens = () => {
    if (typeof item !== 'object' || userPlan === 'free') return [];

    const allergenMap = {
      milk: { name: 'Milk', icon: '🥛' },
      cereals_containing_gluten: { name: 'Gluten', icon: '🌾' },
      eggs: { name: 'Eggs', icon: '🥚' },
      peanuts: { name: 'Peanuts', icon: '🥜' },
      nuts: { name: 'Tree Nuts', icon: '🌰' },
      soybeans: { name: 'Soybeans', icon: '🌱' },
      fish: { name: 'Fish', icon: '🐟' },
      crustaceans: { name: 'Crustaceans', icon: '🦐' },
      molluscs: { name: 'Molluscs', icon: '🦪' },
      sesame_seeds: { name: 'Sesame Seeds', icon: '⚫' },
      celery: { name: 'Celery', icon: '🥬' },
      mustard: { name: 'Mustard', icon: '🌿' },
      sulphur_dioxide_and_sulphites: { name: 'Sulphites', icon: '🧪' },
      lupin: { name: 'Lupin', icon: '🫘' },
    };

    const highRiskAllergens = [];

    Object.entries(allergenMap).forEach(([key, { name, icon }]) => {
      if (item[key] === 'high') {
        highRiskAllergens.push({ name, icon });
      }
    });

    return highRiskAllergens;
  };

  // Function to render dietary emojis
  const renderDietaryEmojis = () => {
    if (typeof item !== 'object') return null;

    const emojis = [];

    if (item.is_vegetarian === true) {
      emojis.push(
        <span key="vegetarian" className="dietary-emoji" title="Vegetarian">
          🥬
        </span>
      );
    }

    if (item.is_vegan === true) {
      emojis.push(
        <span key="vegan" className="dietary-emoji" title="Vegan">
          🌱
        </span>
      );
    }

    return emojis.length > 0 ? emojis : null;
  };

  // Handle card click but prevent when clicking on image navigation
  const handleCardClick = e => {
    // If demo locked, redirect to login
    if (isDemoLocked) {
      window.location.href = '/login';
      return;
    }

    // If locked for free users, redirect to pricing
    if (isLocked) {
      onUpgradeClick();
      return;
    }

    // Don't open modal if clicking on image navigation arrows, know more button, or upgrade link
    if (
      e.target.closest('.image-nav-arrow') ||
      e.target.closest('.know-more-button') ||
      e.target.closest('.upgrade-link') ||
      e.target.closest('.calories-clickable')
    ) {
      return;
    }
    onItemClick(item, index);
  };

  // Handle know more button click
  const handleKnowMoreClick = e => {
    e.stopPropagation();
    if (isDemoLocked) {
      window.location.href = '/login';
      return;
    }
    if (isLocked) {
      onUpgradeClick();
      return;
    }
    onItemClick(item, index);
  };

  // Handle upgrade link click
  const handleUpgradeClick = e => {
    e.stopPropagation();
    onUpgradeClick();
  };

  // Handle demo login button click
  const handleDemoClick = e => {
    e.stopPropagation();
    window.location.href = '/login';
  };

  // Handle calories click/hover
  const handleCaloriesClick = e => {
    if (!approximateCalories) {
      e.stopPropagation();
      onUpgradeClick();
    }
  };

  return (
    <div
      className={`menu-item-card-airbnb ${isDemoLocked ? 'demo-locked' : ''} ${isLocked ? 'free-locked' : ''}`}
      onClick={handleCardClick}
    >
      {/* Demo lock overlay */}
      {isDemoLocked && (
        <div className="demo-lock-overlay">
          <button className="demo-login-button" onClick={handleDemoClick}>
            🔒 Login to see details
          </button>
        </div>
      )}

      {/* Free plan lock overlay */}
      {isLocked && !isDemoLocked && (
        <div className="demo-lock-overlay">
          <button className="demo-login-button" onClick={handleUpgradeClick}>
            🔓 Upgrade to unlock all items
          </button>
        </div>
      )}

      {/* Image Section */}
      <div className="menu-item-image-airbnb">
        {item.loading ? (
          <div className="food-image-placeholder-airbnb loading">
            <span className="spinner"></span>
            <small>Loading images...</small>
          </div>
        ) : imageUrls && imageUrls.length > 0 ? (
          <FoodImage imageUrls={imageUrls} altText={menuName} />
        ) : (
          // Show loading state for menu items that exist but don't have images yet
          <div className="food-image-placeholder-airbnb menu-item-image-loading">
            <div className="loading-spinner"></div>
            <small>Searching for images...</small>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="menu-item-content-airbnb">
        {/* Header with Title and Know More Button */}
        <div className="content-header">
          <div className="food-name-row">
            <h3 className="food-name-airbnb">{menuName}</h3>
          </div>
          <button className="know-more-button" onClick={handleKnowMoreClick} aria-label="Know more about this dish">
            {isDemoLocked ? 'Login' : isLocked ? 'Upgrade' : 'Know more'}
          </button>
        </div>

        {/* Description */}
        {description && (
          <div className="food-description">
            <p className="food-description-text">{description}</p>
          </div>
        )}

        {/* Stats Row - Nutri Score, Dietary Emojis, Calories, and Price */}
        <div className="stats-row-airbnb">
          {/* Nutri Score Display */}
          <div className="nutri-score-section-airbnb">
            <NutriScoreDisplay score={nutritionalScore} size="extra-small" />
          </div>

          {/* Dietary Emojis */}
          <div className="dietary-emojis-row">{renderDietaryEmojis()}</div>

          <div className="calories-airbnb">
            {!approximateCalories ? (
              <div
                className="calories-clickable"
                onClick={handleCaloriesClick}
                onMouseEnter={() => setShowUpgradeTooltip(true)}
                onMouseLeave={() => setShowUpgradeTooltip(false)}
              >
                <span className="calories-text-airbnb">
                  🔥 <span className="blurred">{getRandomCalories()}</span> cal
                </span>
                {showUpgradeTooltip && <div className="upgrade-tooltip">🔒 Upgrade to unlock</div>}
              </div>
            ) : (
              <span className="calories-text-airbnb">🔥 {approximateCalories} cal</span>
            )}
          </div>

          {/* Price moved to stats row */}
          {price && <span className="food-price-airbnb">{price}</span>}
        </div>

        {/* Allergen Warning Section - Now at the bottom */}
        {userPlan !== 'free' && (
          <div className="allergen-warning-section">
            {getHighRiskAllergens().length > 0 ? (
              <>
                <div className="allergen-warning-header">
                  <span className="warning-icon">⚠️</span>
                  <span className="allergen-warning-text">RISK ALLERGENS:</span>
                  <div className="allergen-chips">
                    {getHighRiskAllergens().map((allergen, index) => (
                      <div key={index} className="allergen-chip">
                        <span className="allergen-icon">{allergen.icon}</span>
                        <span className="allergen-name">{allergen.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              /* Empty placeholder for paying users with no high-risk allergens */
              <div className="allergen-warning-placeholder">
                <span className="allergen-warning-text"></span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuItemCard;
